import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './index.css';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';
import { AuthProvider } from './contexts/AuthContext';

// Suppress wallet extension errors (MetaMask, Phantom, etc.)
// These are harmless and don't affect the application
const isWalletExtensionError = (error) => {
  if (!error) return false;
  
  const errorMessage = error.message || error.toString() || '';
  const errorString = errorMessage.toLowerCase();
  
  // Common wallet extension error messages
  const walletErrorPatterns = [
    'user rejected the request',
    'user rejected',
    'user denied',
    'user cancelled',
    'metamask',
    'phantom',
    'keplr',
    'wallet',
    'ethereum provider',
    'solana provider',
    'provider',
    'extension',
    'inpage',
    'content script',
    'walletconnect',
    'coinbase',
  ];
  
  return walletErrorPatterns.some(pattern => errorString.includes(pattern));
};

// Handle unhandled promise rejections (common for wallet extensions)
window.addEventListener('unhandledrejection', (event) => {
  
  
  if (isWalletExtensionError(event.reason)) {
    event.preventDefault(); // Suppress the error
    console.debug('Suppressed wallet extension error:', event.reason);
    return;
  }
  // Log non-wallet errors for debugging
  console.error('Unhandled promise rejection:', event.reason);
});

// Handle general errors (less common but can happen)
const originalErrorHandler = window.onerror;
window.onerror = (message, source, lineno, colno, error) => {
  
  
  if (isWalletExtensionError(error || message)) {
    return true; // Suppress the error
  }
  // Call original handler if it exists
  if (originalErrorHandler) {
    return originalErrorHandler(message, source, lineno, colno, error);
  }
  return false;
};

// Suppress React error overlay for wallet extension errors
// This intercepts errors before React's error overlay displays them
if (process.env.NODE_ENV === 'development') {
  // Override console.error to suppress wallet errors in React's error overlay
  const originalConsoleError = console.error;
  console.error = (...args) => {
    const errorString = args.map(arg => 
      typeof arg === 'object' && arg !== null 
        ? (arg.message || arg.toString() || '')
        : String(arg)
    ).join(' ').toLowerCase();
    
    if (errorString.includes('user rejected') || 
        errorString.includes('wallet') ||
        errorString.includes('extension') ||
        errorString.includes('metamask') ||
        errorString.includes('phantom')) {
      // Suppress wallet extension errors
      console.debug('Suppressed wallet extension error in console.error:', args);
      return;
    }
    
    // Call original console.error for non-wallet errors
    originalConsoleError.apply(console, args);
  };
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>
);

